<script>
  import { portal } from "svelte-portal";
  import RSAKey from "../lib/rsa";
  import { hex2b64 } from "../lib/base64";
  import { TYPO3_STATUS, NEOS_STATUS, LOGIN_STATUS } from "../lib/localstore";
  import { operationStore, query } from "@urql/svelte";
  import { USER_INFO } from "../Api/user";
  import { fade, scale } from "svelte/transition";
  import Button from "./Button.svelte";
  import IconButton from "./IconButton.svelte";

  export let registerEventListener = true;

  const HTML_CLASS_LIST = document.documentElement.classList;
  const OVERFLOW_CLASS = "overflow-hidden";
  const OVERLAY_CLASS = "has-overlay";

  let showModal = false;

  let passwordInput = "";
  let usernameInput = "";
  let permaloginInput = 1;
  const REGISTER_HREF = "/mandate/mandatetool-registrierung";
  const FORGET_HREF = "/zugangsdaten-anfordern";
  const EDIT_PROFILE_HREF = "/index.php?id=755";

  $: if (showModal) {
    HTML_CLASS_LIST.add(OVERFLOW_CLASS, OVERLAY_CLASS);
  } else {
    HTML_CLASS_LIST.remove(OVERFLOW_CLASS, OVERLAY_CLASS);
  }

  const NEOS_USER_DATA = operationStore(USER_INFO);

  query(NEOS_USER_DATA);

  NEOS_USER_DATA.subscribe((storeObj) => {
    if (storeObj.fetching) {
      return;
    }
    if (storeObj.data !== undefined) {
      NEOS_STATUS.set(true);
    }
  });

  LOGIN_STATUS.subscribe((value) => {
    if (value === true) {
      showModal = false;
    }
  });

  function openLoginModal() {
    if (registerEventListener) {
      showModal = true;
    }
  }

  function freshLogin() {
    TYPO3_STATUS.set(false);
    NEOS_STATUS.set(false);
    showModal = true;
  }

  async function logout() {
    TYPO3_STATUS.set("pending");
    NEOS_STATUS.set("pending");
    dispatchEvent("ajaxify:clear");

    // Log out from TYPO3
    const TYPO3_DATA = new URLSearchParams({
      logintype: "logout",
    });

    await Promise.allSettled([
      // Log out from Neos
      fetch("/neos/authentication/logout", {
        method: "GET",
        mode: "cors",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      fetch("/fpiAuth", {
        method: "POST",
        credentials: "include",
        body: TYPO3_DATA,
      }),
    ]).finally(() => {
      // The finally() method of Promise instances schedules a function to be called when the promise is settled (either fulfilled or rejected)
      TYPO3_STATUS.set(false);
      NEOS_STATUS.set(false);
      setTimeout(() => {
        if (window.location.pathname == "/") {
          dispatchEvent("ajaxify:reload");
          return;
        }
        window.location.href = "/";
      }, 10);
    });
  }

  function loginSuccessfull(system) {
    dispatchEvent("ajaxify:reload");
    if (system == "TYPO3") {
      TYPO3_STATUS.set(true);
      dispatchEvent("loginSuccessfull");
      return;
    }
    NEOS_STATUS.set(true);
  }

  function dispatchEvent(name, detail = {}) {
    window.dispatchEvent(new CustomEvent(name, detail));
  }

  function login() {
    TYPO3_STATUS.set("pending");
    NEOS_STATUS.set("pending");

    // TODO: Use Promise.allSettled

    // Log into Neos
    fetch("/neos/authentication/token-auth", {
      method: "POST",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: usernameInput,
        password: passwordInput,
      }),
    }).then((response) => {
      if (!response.ok) {
        // make the promise be rejected if we didn't get a 2xx response
        const err = new Error("Not 2xx response");
        err.response = response;
        NEOS_STATUS.set("failed");
        throw err;
      }
      return response.json().then((data) => {
        loginSuccessfull("Neos");
        return { status: response.status, body: data };
      });
    });

    // Log into TYPO3
    fetch("/index.php?eID=FrontendLoginRsaPublicKey", {
      method: "GET",
      credentials: "include",
    }).then((response) => {
      if (!response.ok) {
        // make the promise be rejected if we didn't get a 2xx response
        const err = new Error("No 2xx response");
        err.response = response;
        TYPO3_STATUS.set("failed");
        throw err;
      }

      response.text().then((value) => {
        const publicKey = value.split(":");
        const rsa = new RSAKey();
        rsa.setPublic(publicKey[0], publicKey[1]);
        const encryptedPassword = rsa.encrypt(passwordInput);
        const passwordValue = "rsa:" + hex2b64(encryptedPassword);
        const data = new URLSearchParams({
          user: usernameInput,
          pass: passwordValue,
          pid: 685,
          logintype: "login",
          redirect_url: "",
          permalogin: permaloginInput,
        });
        fetch("/fpiAuth", {
          method: "POST",
          credentials: "include",
          body: data,
        }).then((response) => {
          if (!response.ok || !document.cookie.includes("fe_typo_user=")) {
            // make the promise be rejected if we didn't get a 2xx response
            // or we have no cookie
            const err = new Error("Not 2xx response");
            err.response = response;
            TYPO3_STATUS.set("failed");
            throw err;
          }

          loginSuccessfull("TYPO3");
        });
      });
    });
  }
</script>

<svelte:window
  on:openLogin={openLoginModal}
  on:openLoginModal={openLoginModal}
  on:keyup={(event) => {
    if (event.key === "Escape") {
      showModal = false;
    }
  }}
/>

{#if showModal}
  <div
    transition:fade={{ duration: 200 }}
    use:portal={"body"}
    aria-hidden="true"
    on:click={(event) => {
      event.stopPropagation();
    }}
    class="fixed inset-0 z-60 bg-black/80 backdrop-blur supports-[backdrop-filter]:bg-black/75"
  />
  <div
    transition:fade={{ duration: 200 }}
    use:portal={"body"}
    role="dialog"
    aria-modal="true"
    class="pointer-events-none fixed inset-0 z-70 grid grid-rows-[minmax(0,1fr)_auto_minmax(0,1fr)] overflow-auto py-8 text-theme-minor-50"
  >
    <button
      title="Schließen"
      type="button"
      class="pointer-events-auto fixed right-4 top-4 text-5xl opacity-80 hocus:opacity-100"
      on:click={() => {
        showModal = false;
      }}>&times;</button
    >
    <div transition:scale={{ duration: 200 }} class="pointer-events-auto row-start-2 flex min-h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      {#if $LOGIN_STATUS !== true}
        <form class="relative w-full max-w-2xl space-y-8 bg-white px-10 py-20" class:cursor-progress={$LOGIN_STATUS === "pending"} on:submit|preventDefault={login}>
          <div class="mx-auto max-w-xl" class:pointer-events-none={$LOGIN_STATUS === "pending"}>
            <img class="mx-auto h-20 w-auto" src="/_Resources/Static/Packages/Fpi.Im.Theme/Assets/LogoClaim.svg" alt="Logo von Institutional Money" />
            <h2 class="mt-6 text-center text-3xl font-bold text-gray-900">Loggen Sie sich mit Ihrem Account ein</h2>
            <p class="mt-2 text-center text-sm text-gray-600 transition" class:blur-sm={$LOGIN_STATUS === "pending"}>
              oder
              <a href={REGISTER_HREF} class="font-medium text-theme-signal hover:underline"> registrieren Sie sich </a>
            </p>
          </div>

          <div class="mx-auto mt-8 max-w-xl space-y-6" class:pointer-events-none={$LOGIN_STATUS === "pending"}>
            <input type="hidden" name="remember" value="true" />
            <div class="-space-y-px rounded-sm shadow-sm transition" class:blur-sm={$LOGIN_STATUS === "pending"}>
              <div>
                <label for="username" class="sr-only">Benutzername</label>
                <input
                  bind:value={usernameInput}
                  type="text"
                  name="username"
                  autocomplete="email"
                  required
                  class="relative block w-full appearance-none rounded-none rounded-t-sm border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-theme-signal focus:outline-none focus:ring-theme-signal sm:text-sm"
                  placeholder="Benutzername"
                />
              </div>
              <div>
                <label for="password" class="sr-only">Passwort</label>
                <input
                  bind:value={passwordInput}
                  type="password"
                  name="password"
                  autocomplete="current-password"
                  required
                  class="relative block w-full appearance-none rounded-none rounded-b-sm border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-theme-signal focus:outline-none focus:ring-theme-signal sm:text-sm"
                  placeholder="Passwort"
                />
              </div>
            </div>

            <div class="flex items-center justify-between transition" class:blur-sm={$LOGIN_STATUS === "pending"}>
              <div class="text-sm">
                <a href={FORGET_HREF} class="font-medium text-theme-signal hover:underline"> Zugangsdaten zusenden </a>
              </div>
            </div>

            <div>
              {#if $LOGIN_STATUS === "pending"}
                <button
                  class="relative flex w-full cursor-progress justify-center rounded-sm border border-transparent bg-theme-signal px-4 py-2 text-sm font-medium text-white transition-opacity focus:outline-none"
                  disabled
                >
                  <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 animate-pulse fill-current" viewBox="0 0 20 20">
                      <path
                        fill-rule="evenodd"
                        d="M6.625 2.655A9 9 0 0119 11a1 1 0 11-2 0 7 7 0 00-9.625-6.492 1 1 0 11-.75-1.853zM4.662 4.959A1 1 0 014.75 6.37 6.97 6.97 0 003 11a1 1 0 11-2 0 8.97 8.97 0 012.25-5.953 1 1 0 011.412-.088z"
                        clip-rule="evenodd"
                      />
                      <path
                        fill-rule="evenodd"
                        d="M5 11a5 5 0 1110 0 1 1 0 11-2 0 3 3 0 10-6 0c0 1.677-.345 3.276-.968 4.729a1 1 0 11-1.838-.789A9.964 9.964 0 005 11zm8.921 2.012a1 1 0 01.831 1.145 19.86 19.86 0 01-.545 2.436 1 1 0 11-1.92-.558c.207-.713.371-1.445.49-2.192a1 1 0 011.144-.83z"
                        clip-rule="evenodd"
                      />
                      <path fill-rule="evenodd" d="M10 10a1 1 0 011 1c0 2.236-.46 4.368-1.29 6.304a1 1 0 01-1.838-.789A13.952 13.952 0 009 11a1 1 0 011-1z" clip-rule="evenodd" />
                    </svg>
                  </span>
                  <span>Bitte warten…</span>
                </button>
              {:else}
                <button
                  type="submit"
                  class="relative flex w-full cursor-pointer justify-center rounded-sm border border-transparent bg-theme-signal px-4 py-2 text-sm font-medium text-white transition-opacity focus:outline-none focus:ring-2 focus:ring-theme-signal focus:ring-offset-2 disabled:cursor-not-allowed disabled:!opacity-50 hocus:opacity-90"
                >
                  <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="h-5 w-5 fill-current">
                      <path
                        d="M416 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c17.67 0 32 14.33 32 32v256c0 17.67-14.33 32-32 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c53.02 0 96-42.98 96-96V128C512 74.98 469 32 416 32zM342.6 233.4l-128-128c-12.51-12.51-32.76-12.49-45.25 0c-12.5 12.5-12.5 32.75 0 45.25L242.8 224H32C14.31 224 0 238.3 0 256s14.31 32 32 32h210.8l-73.38 73.38c-12.5 12.5-12.5 32.75 0 45.25s32.75 12.5 45.25 0l128-128C355.1 266.1 355.1 245.9 342.6 233.4z"
                      />
                    </svg>
                  </span>
                  Anmelden
                </button>
              {/if}
            </div>
          </div>
          {#if $LOGIN_STATUS === "failed"}
            <p transition:fade={{ duration: 200 }} class="absolute inset-x-0 bottom-0 bg-theme-signal p-2 text-center text-white">
              Das Login ist leider fehlgeschlagen. Bitte versuchen Sie es nochmals.
            </p>
          {/if}
        </form>
      {:else}
        <iframe class="h-[calc(95vh-theme(height.28))] w-full" src={EDIT_PROFILE_HREF} title="Daten ändern" name="neos-iframe" />
      {/if}
    </div>
  </div>
{/if}
{#if $LOGIN_STATUS === true}
  <div class="flex">
    <Button
      on:click={() => {
        showModal = true;
      }}
      viewBox="0 0 448 512"
    >
      <path
        slot="path"
        d="M224 256c70.7 0 128-57.31 128-128s-57.3-128-128-128C153.3 0 96 57.31 96 128S153.3 256 224 256zM274.7 304H173.3C77.61 304 0 381.6 0 477.3c0 19.14 15.52 34.67 34.66 34.67h378.7C432.5 512 448 496.5 448 477.3C448 381.6 370.4 304 274.7 304z"
      />
      Daten ändern
    </Button>
    <IconButton on:click={logout} tooltip="Ausloggen">
      <path
        d="M160 416H96c-17.67 0-32-14.33-32-32V128c0-17.67 14.33-32 32-32h64c17.67 0 32-14.33 32-32S177.7 32 160 32H96C42.98 32 0 74.98 0 128v256c0 53.02 42.98 96 96 96h64c17.67 0 32-14.33 32-32S177.7 416 160 416zM502.6 233.4l-128-128c-12.51-12.51-32.76-12.49-45.25 0c-12.5 12.5-12.5 32.75 0 45.25L402.8 224H192C174.3 224 160 238.3 160 256s14.31 32 32 32h210.8l-73.38 73.38c-12.5 12.5-12.5 32.75 0 45.25s32.75 12.5 45.25 0l128-128C515.1 266.1 515.1 245.9 502.6 233.4z"
      />
    </IconButton>
  </div>
{:else if $LOGIN_STATUS === "pending"}
  <Button disabled svgClass="animate-pulse fill-current h-4 w-4" viewBox="0 0 20 20" class="!cursor-progress">
    <svelte:fragment slot="path">
      <path
        fill-rule="evenodd"
        d="M6.625 2.655A9 9 0 0119 11a1 1 0 11-2 0 7 7 0 00-9.625-6.492 1 1 0 11-.75-1.853zM4.662 4.959A1 1 0 014.75 6.37 6.97 6.97 0 003 11a1 1 0 11-2 0 8.97 8.97 0 012.25-5.953 1 1 0 011.412-.088z"
        clip-rule="evenodd"
      />
      <path
        fill-rule="evenodd"
        d="M5 11a5 5 0 1110 0 1 1 0 11-2 0 3 3 0 10-6 0c0 1.677-.345 3.276-.968 4.729a1 1 0 11-1.838-.789A9.964 9.964 0 005 11zm8.921 2.012a1 1 0 01.831 1.145 19.86 19.86 0 01-.545 2.436 1 1 0 11-1.92-.558c.207-.713.371-1.445.49-2.192a1 1 0 011.144-.83z"
        clip-rule="evenodd"
      />
      <path fill-rule="evenodd" d="M10 10a1 1 0 011 1c0 2.236-.46 4.368-1.29 6.304a1 1 0 01-1.838-.789A13.952 13.952 0 009 11a1 1 0 011-1z" clip-rule="evenodd" />
    </svelte:fragment>
    Bitte warten…
  </Button>
{:else}
  <Button on:click={freshLogin}>
    <path
      slot="path"
      d="M416 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c17.67 0 32 14.33 32 32v256c0 17.67-14.33 32-32 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c53.02 0 96-42.98 96-96V128C512 74.98 469 32 416 32zM342.6 233.4l-128-128c-12.51-12.51-32.76-12.49-45.25 0c-12.5 12.5-12.5 32.75 0 45.25L242.8 224H32C14.31 224 0 238.3 0 256s14.31 32 32 32h210.8l-73.38 73.38c-12.5 12.5-12.5 32.75 0 45.25s32.75 12.5 45.25 0l128-128C355.1 266.1 355.1 245.9 342.6 233.4z"
    />
    Anmelden
  </Button>
{/if}
